import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Permission } from "../../api/services/permissions/type";
import { Role } from "../../api/services/roles/type";
import { useAllMenus } from "../../hooks/useAllMenus";

interface RolePermissionTableProps {
  roles: Role[];
  permissions: Permission[];
  rolePermissions: Record<string, string[]>;
  onPermissionToggle: (
    roleId: string,
    permissionId: string,
    hasPermission: boolean,
  ) => void;
  onMenuToggle?: (roleId: string, menuId: string, hasAccess: boolean) => void;
  isLoading?: boolean;
  isUpdating?: boolean;
}

export const RolePermissionTable: React.FC<RolePermissionTableProps> = ({
  roles,
  permissions,
  rolePermissions,
  onPermissionToggle,
  onMenuToggle,
  isLoading = false,
  isUpdating = false,
}) => {
  const { data: availableMenus = [], isLoading: menusLoading } = useAllMenus();

  // Create combined menu list from available menus and role sidebar_menus
  const getAllMenusForTable = () => {
    const menuMap = new Map();

    // Add available menus first
    availableMenus.forEach((menu) => {
      menuMap.set(menu.id, menu);
    });

    // Add menus from roles' sidebar_menus that might not be in availableMenus
    roles.forEach((role) => {
      if (role.sidebar_menus) {
        role.sidebar_menus.forEach((roleMenu) => {
          // Check if we already have this menu by ID
          if (!menuMap.has(roleMenu.id)) {
            // Check if we have it by name/route (to avoid duplicates)
            const existsByNameRoute = Array.from(menuMap.values()).some(
              (existing) =>
                existing.menu_name === roleMenu.menu_name &&
                existing.route_path === roleMenu.route_path,
            );

            if (!existsByNameRoute) {
              menuMap.set(roleMenu.id, roleMenu);
            }
          }
        });
      }
    });

    return Array.from(menuMap.values());
  };

  const allMenusForTable = getAllMenusForTable();

  // State for collapsible sections
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [sidebarMenusCollapsed, setSidebarMenusCollapsed] = useState(false);

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const groupedPermissions = permissions.reduce(
    (acc, permission) => {
      if (!acc[permission.tableName]) {
        acc[permission.tableName] = [];
      }
      acc[permission.tableName].push(permission);
      return acc;
    },
    {} as Record<string, Permission[]>,
  );

  const hasPermission = (roleId: string, permissionId: string): boolean => {
    return rolePermissions[roleId]?.includes(permissionId) || false;
  };

  const hasMenuAccess = (roleId: string, menuId: string): boolean => {
    const role = roles.find((r) => r.id === roleId);

    // Check multiple possible formats:
    // 1. sidebarMenuIds array (new format)
    if (role?.sidebarMenuIds?.includes(menuId)) {
      return true;
    }

    // 2. sidebar_menus objects with direct menu ID match
    if (role?.sidebar_menus?.some((menu) => menu.id === menuId)) {
      return true;
    }

    // 3. sidebar_menus objects where we need to match by menu_name and route_path
    // This handles cases where the available menus and role menus have different IDs
    const tableMenu = allMenusForTable.find((m) => m.id === menuId);
    if (
      tableMenu &&
      role?.sidebar_menus?.some(
        (roleMenu) =>
          roleMenu.menu_name === tableMenu.menu_name &&
          roleMenu.route_path === tableMenu.route_path,
      )
    ) {
      return true;
    }

    return false;
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-6 gap-4">
            {Array.from({ length: 24 }).map((_, i) => (
              <div key={i} className="h-6 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-slate-700 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            Roles & Permissions
          </h2>
          <div className="flex items-center gap-4">
            {isUpdating && (
              <div className="flex items-center gap-2 text-blue-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                <span className="text-sm">Updating...</span>
              </div>
            )}
            <div className="text-sm text-slate-400">
              {roles.length} roles • {permissions.length} permissions
            </div>
          </div>
        </div>
      </div>

      {/* Table Container with horizontal scroll */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max">
          {/* Table Header */}
          <thead>
            <tr className="bg-slate-700 border-b border-slate-600">
              <th className="sticky left-0 bg-slate-700 z-20 text-left p-4 font-medium text-slate-200 w-80">
                Permissions
              </th>
              {roles.map((role) => (
                <th
                  key={role.id}
                  className="text-center p-4 font-medium text-slate-200 min-w-[120px]"
                >
                  <div className="text-sm font-semibold">{role.name}</div>
                  <div className="text-xs text-slate-400 mt-1">Role</div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-slate-700">
            {Object.entries(groupedPermissions).map(
              ([tableName, tablePermissions]) => (
                <React.Fragment key={tableName}>
                  {/* Table Section Header with Toggle */}
                  <tr className="bg-slate-750">
                    <td className="sticky left-0 bg-slate-750 z-10 p-3 font-medium text-slate-300">
                      <button
                        onClick={() => toggleGroup(tableName)}
                        className="flex items-center gap-2 hover:text-white transition-colors w-full text-left"
                      >
                        {collapsedGroups[tableName] ? (
                          <ChevronRight size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                        <span className="capitalize">
                          {tableName.replace(/([A-Z])/g, " $1").trim()}
                        </span>
                        <span className="text-xs text-slate-500 ml-auto">
                          ({tablePermissions.length})
                        </span>
                      </button>
                    </td>
                    {roles.map((role) => (
                      <td key={role.id} className="p-3">
                        {/* Empty cell for section headers */}
                      </td>
                    ))}
                  </tr>

                  {/* Permission Rows - Only show if not collapsed */}
                  {!collapsedGroups[tableName] &&
                    tablePermissions.map((permission) => (
                      <tr
                        key={permission.id}
                        className="hover:bg-slate-750 transition-colors"
                      >
                        {/* Permission Name - Sticky Left Column */}
                        <td className="sticky left-0 bg-slate-800 hover:bg-slate-750 transition-colors z-10 p-4 w-80">
                          <div className="text-slate-200 font-medium">
                            {permission.description}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {permission.action} • {permission.tableName}
                          </div>
                        </td>

                        {/* Role Permission Checkboxes */}
                        {roles.map((role) => {
                          const roleHasPermission = hasPermission(
                            role.id,
                            permission.id,
                          );
                          return (
                            <td key={role.id} className="p-4 text-center">
                              {/* Custom styled checkbox */}
                              <div
                                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all cursor-pointer mx-auto ${
                                  isUpdating
                                    ? "opacity-50 cursor-not-allowed"
                                    : "cursor-pointer"
                                } ${
                                  roleHasPermission
                                    ? "bg-blue-600 border-blue-600"
                                    : "border-slate-500 hover:border-slate-400"
                                }`}
                                onClick={() => {
                                  if (!isUpdating) {
                                    onPermissionToggle(
                                      role.id,
                                      permission.id,
                                      !roleHasPermission,
                                    );
                                  }
                                }}
                              >
                                {roleHasPermission && (
                                  <svg
                                    className="w-4 h-4 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                </React.Fragment>
              ),
            )}

            {/* Sidebar Menus Section */}
            {!menusLoading && allMenusForTable.length > 0 && (
              <React.Fragment>
                {/* Sidebar Menus Header with Toggle */}
                <tr className="bg-slate-750">
                  <td className="sticky left-0 bg-slate-750 z-10 p-3 font-medium text-slate-300">
                    <button
                      onClick={() =>
                        setSidebarMenusCollapsed(!sidebarMenusCollapsed)
                      }
                      className="flex items-center gap-2 hover:text-white transition-colors w-full text-left"
                    >
                      {sidebarMenusCollapsed ? (
                        <ChevronRight size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                      <span>🔗 Sidebar Menus</span>
                      <span className="text-xs text-slate-500 ml-auto">
                        ({allMenusForTable.length})
                      </span>
                    </button>
                  </td>
                  {roles.map((role) => (
                    <td key={role.id} className="p-3">
                      {/* Empty cell for section headers */}
                    </td>
                  ))}
                </tr>

                {/* Menu Rows - Only show if not collapsed */}
                {!sidebarMenusCollapsed &&
                  allMenusForTable.map((menu) => (
                    <tr
                      key={menu.id}
                      className="hover:bg-slate-750 transition-colors"
                    >
                      {/* Menu Name - Sticky Left Column */}
                      <td className="sticky left-0 bg-slate-800 hover:bg-slate-750 transition-colors z-10 p-4 w-80">
                        <div className="text-slate-200 font-medium">
                          {menu.menu_name}
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {menu.route_path}
                        </div>
                      </td>

                      {/* Role Menu Access Checkboxes */}
                      {roles.map((role) => {
                        const roleHasMenuAccess = hasMenuAccess(
                          role.id,
                          menu.id,
                        );
                        return (
                          <td key={role.id} className="p-4 text-center">
                            {/* Custom styled checkbox for menu access */}
                            <div
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all cursor-pointer mx-auto ${
                                isUpdating || !onMenuToggle
                                  ? "opacity-50 cursor-not-allowed"
                                  : "cursor-pointer"
                              } ${
                                roleHasMenuAccess
                                  ? "bg-green-600 border-green-600"
                                  : "border-slate-500 hover:border-slate-400"
                              }`}
                              onClick={() => {
                                if (!isUpdating && onMenuToggle) {
                                  onMenuToggle(
                                    role.id,
                                    menu.id,
                                    !roleHasMenuAccess,
                                  );
                                }
                              }}
                            >
                              {roleHasMenuAccess && (
                                <svg
                                  className="w-4 h-4 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
